/**
 * Scrub preview (spec 06 card 2): hovering the chapter bar shows a 180px card with the video frame at
 * that second, who is speaking, the time and the agenda item, so you can find a moment without playing.
 * Also on keyboard focus (←/→ move 10 s, Shift 60 s, Enter jumps there) and on touch (drag along the bar).
 * Frames come from the snapshot's `frames` (nearest at or before that second), else the video thumbnail.
 */
import { useEffect, useState, type RefObject } from "react";
import { meetingStore } from "../../stores/meetingStore";
import { Face } from "../shared/Face";
import { clock } from "../../lib/format";
import { chapterAt } from "./skipProcedure";
import { seekTo } from "./vimeoClock";

const CARD_W = 180;

function frameAt(t: number): string | undefined {
  const s = meetingStore.getState();
  const frames = s.frames;
  let lo = 0, hi = frames.length - 1, ans = -1;
  while (lo <= hi) { const mid = (lo + hi) >> 1; if (frames[mid].t <= t) { ans = mid; lo = mid + 1; } else hi = mid - 1; }
  return ans >= 0 ? frames[ans].url : frames[0]?.url ?? s.meeting?.video?.thumbnailUrl;
}

export function ScrubPreview({ trackRef, duration }: { trackRef: RefObject<HTMLDivElement | null>; duration: number }) {
  const [t, setT] = useState<number | null>(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const at = (clientX: number) => {
      const r = el.getBoundingClientRect();
      return Math.min(duration, Math.max(0, ((clientX - r.left) / r.width) * duration));
    };
    const move = (e: PointerEvent) => { if (e.pointerType === "mouse" || e.buttons) setT(at(e.clientX)); };
    const leave = () => setT(null);
    const focus = () => { if (el.matches(":focus-visible")) setT(meetingStore.getState().currentTime); };
    const key = (e: KeyboardEvent) => {
      const step = e.shiftKey ? 60 : 10;
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault();
        setT((v) => Math.min(duration, Math.max(0, (v ?? meetingStore.getState().currentTime) + (e.key === "ArrowRight" ? step : -step))));
      } else if (e.key === "Enter") {
        setT((v) => { if (v != null) seekTo(v); return v; });
      } else if (e.key === "Escape") setT(null);
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerdown", move);
    el.addEventListener("pointerleave", leave);
    el.addEventListener("focus", focus);
    el.addEventListener("blur", leave);
    el.addEventListener("keydown", key);
    return () => {
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerdown", move);
      el.removeEventListener("pointerleave", leave);
      el.removeEventListener("focus", focus);
      el.removeEventListener("blur", leave);
      el.removeEventListener("keydown", key);
    };
  }, [trackRef, duration]);

  if (t == null || !trackRef.current) return null;
  const s = meetingStore.getState();
  const width = trackRef.current.clientWidth;
  const x = (t / duration) * width;
  const left = Math.min(width - CARD_W, Math.max(0, x - CARD_W / 2));
  const turn = s.turns.find((u) => u.start <= t && t < u.end);
  const person = turn ? s.people[turn.personId] : undefined;
  const ci = chapterAt(s.chapters, t);
  const chapter = ci >= 0 ? s.chapters[ci] : undefined;
  const frame = frameAt(t);
  return (
    <div className="scrub-card" style={{ left }} aria-hidden>
      <div className="scrub-frame" style={frame ? { backgroundImage: `url(${frame})` } : undefined} />
      <div className="scrub-info">
        {person && <Face name={person.name} photoUrl={person.photoUrl} size={16} />}
        <span className="scrub-text">
          {person && <span className="scrub-name">{person.name}</span>}
          <span className="scrub-meta">{[clock(t), chapter && (chapter.shortTitle ?? chapter.title)].filter(Boolean).join(" · ")}</span>
        </span>
      </div>
    </div>
  );
}
